import {
  Box,
  Fade,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import ClickableImage from "../..//layout/Interactive/Images/ClickableImage";
import { formatDistanceToNow } from "date-fns";

const renderValues = {
  style: {
    title: "Style",
    color: "grey",
    variant: "body",
  },
  graduated_proposals: {
    title: null,
    variant: "h3",
    color: "grey",
  },
};

const TimestampComponent = ({ timestamp, isQaTimestamp=false }) => {
  const timeAgo = formatDistanceToNow(new Date(timestamp), { addSuffix: true });

  return <Typography variant="body" color="inherit">{isQaTimestamp ? `QA'd` : `Proposed`} {timeAgo}</Typography>;
};

const Proposal = ({ name, user, status, confidence, timestamp, qa_timestamp}) => {
  let backgroundColor = "white";
  switch (status) {
    case "PENDING":
      backgroundColor = "blue";
      break;
    case "APPROVED":
      backgroundColor = "green";
      break;
    case "DECLINED":
      backgroundColor = "red";
      break;
    default:
      backgroundColor = "orange";
      break;
  }
  let display_confidence = 0;
  try {
    display_confidence = Math.round(confidence * 100);
  } catch (error) {}

  return (
    <Tooltip
      placement="top"
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -12], // [horizontal offset, vertical offset]
            },
          },
        ],
      }}
      title={
        <React.Fragment >
          <Typography variant="h8" color="inherit" 
            sx={{
              userSelect: "text",
            }}>
            <b>{name}</b>
          </Typography>{" "}
          {/* Name of the proposal */}
          <Typography variant="body" color="inherit">{user}</Typography>{" "}
          <br />
          {/* User information */}
          <Typography variant="body" color="inherit">{status}</Typography>{" "}
          <br />
          <Typography variant="body" color="inherit">{display_confidence}%</Typography>{" "}
          <br />
          <TimestampComponent timestamp={timestamp} isQaTimestamp={false} />{" "}
          <br />
          {qa_timestamp && <TimestampComponent timestamp={qa_timestamp} isQaTimestamp={true} />}{" "}
          {/* Status of the proposal */}
        </React.Fragment>
      }
      sx={{
        width: "100%"
      }}
    >
      <Paper
        sx={{
          border: `2px solid ${backgroundColor}`,
          display: "flex",
          flexDirection: "column",
          p: 0.5,
          py: 0,
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h8"
          sx={{
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 1,
            justifyContent: "space-between"
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={8} 
                sx={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
              }}>
              <Typography
                variant="body_sm"
                sx={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {name}
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography
                variant="body_sm"
                sx={{
                  overflow: "hidden",
                }}
              >
                {display_confidence}%
              </Typography>
            </Grid>
          </Grid>
        </Typography>

        <Typography
          variant="body_sm"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {user}
        </Typography>
      </Paper>
    </Tooltip>
  );
};
const PlainText = ({ value_type, value }) => {
  if (!renderValues[value_type]) return;
  const { title, color, variant } = renderValues[value_type];
  return (
    <Typography variant={variant} sx={{ color: color }}>
      {title ? `${title}: ` : null} {value}
    </Typography>
  );
};
function DataPointRenderComponent(props) {
  const { value, value_type, showProposals } = props;
  let uniqueProposals = value
  switch (value_type) {
    case "graduated_proposals":
      uniqueProposals = value ? [...new Set(value.split(",").map(s => s.trim()))].join(", ") : "";
      break
    default:
      uniqueProposals = value;
      break;
  }

  switch (typeof value) {
    case "string":
      return <PlainText value={uniqueProposals} value_type={value_type} />;
    case "object":
      if (value_type === "proposals") {
        return (
          <div style={{ position: "relative", height: "100%" }}>
            {/* Ensure it has a height */}
            <Fade in={showProposals} timeout={500}>
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  bottom: 0, // Positions the box at the bottom
                  left: 0,
                  right: 0,
                  zIndex: 2, // Ensure it's above other content
                  borderRadius: 3
                }}
              >
                <Grid container spacing={0} sx={{
                  overflowY: "scroll",
                  maxHeight: "7rem",
                  mb: -2
                }}>
                  {value?.map((proposal) => {
                    const { name, user, status } = proposal;
                    return (
                      <Grid
                        item
                        xs={6}
                        sx={{
                          p: 0.25
                        }}
                      >
                        <Proposal {...proposal} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Fade>
          </div>
        );
      }
      return null;
    default:
      return null;
  }
}

function GarmentCard({ garmentData, fixProposals, imageNum = null, showBrand = true, showTitle = true }) {
  const { proposals, garment_id, uuid_public_id, confidence, brand, title } =
    garmentData;
  const dataKeys = Object.keys(garmentData);

  const [showProposals, setShowProposals] = useState();
  const image = null;
  const { palette } = useTheme();

  return (
    <Paper
      sx={{
        height: "250px", // Fixed height
        p: 1,
        m: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      onMouseEnter={() => setShowProposals(true)}
      onMouseLeave={() => setShowProposals(false)}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          bgcolor: "rgba(255,255,255,0.5) !important",
        }}
      >        
        {showBrand && 
        <Typography variant="body_sm"sx={{flex: 12,
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",}}>
          <b>BRAND:</b> {brand}
        </Typography>}
        
        {showTitle && 
        <Typography variant="body_sm" sx={{flex: 12,
          textOverflow: "ellipsis",
          overflow: "hidden",
            whiteSpace: "nowrap",
          userSelect: "text"}}>
          <b>TITLE:</b> {title}
        </Typography>}
      </Box>

      <Box
        sx={{
          borderRadius: 0.1,
          width: "100%",
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          my: 0.5,
        }}
      >
        <ClickableImage
          image={`https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${uuid_public_id}-${
            imageNum ?? 1
          }.jpg`}
        />
      </Box>

      <Box
        sx={{
          justifyContent: "flex-end",
          textAlign: "right",
          backgroundColor: "rgba(255,255,255,0.5)",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: "11px",
            userSelect: "text",
          }}
        >
          ID: {garment_id}
        </Typography>
        {confidence ? (
          <Typography
            variant="body1"
            sx={{
              fontSize: "11px",
              userSelect: "text"
            }}
          >
            (x100) {Math.round(confidence * 100)}%
          </Typography>
        ) : null}
      </Box>

      <Box>
        {dataKeys.map((value_type) => (
          <DataPointRenderComponent
            value={garmentData[value_type]}
            value_type={value_type}
            showProposals={showProposals || fixProposals}
          />
        ))}
      </Box>
    </Paper>
  );
}

export default GarmentCard;
